import React, { useState, useCallback } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';

// Types
import { TenantsAdminLoaderData } from './types';

// GraphQL
import { FETCH_FEATURE_FLAGS } from '../../../../gql/queries/admin';
import {
  CREATE_FEATURE_FLAG,
  DELETE_FEATURE_FLAG,
  UPDATE_FEATURE_FLAG,
} from '../../../../gql/mutations/admin';

// Components
import { Typography } from '../../../../components/display/Typography';
import InputText from '../../../../components/inputs/InputText';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
import SimpleSwitch from '../../../../components/inputs/buttons/SwitchButton/Simple';

// Styles
import {
  Container,
  AdminSection,
  TenantRow,
  TenantInfo,
  StatusBadge,
  FlagRow,
  FlagInfo,
  FlagActions,
  InputsContainer,
  FlagEditFields,
} from './styles';

type FeatureFlagType = {
  id: string;
  key: string;
  description: string;
  defaultValue: string;
  isActive: boolean;
};

export default function TenantsAdminPage(): JSX.Element {
  const { t } = useTranslation();
  const loaderData = useAsyncValue() as TenantsAdminLoaderData;
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [newFlagKey, setNewFlagKey] = useState('');
  const [newFlagDescription, setNewFlagDescription] = useState('');
  const [newFlagDefault, setNewFlagDefault] = useState('false');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editDefaultValue, setEditDefaultValue] = useState('');

  // Fetch global feature flag catalog
  const {
    data: flagsData,
    refetch: refetchFlags,
  } = useQuery(FETCH_FEATURE_FLAGS, { fetchPolicy: 'network-only' });

  const featureFlags: FeatureFlagType[] = flagsData?.featureFlags || [];

  // Mutations
  const [createFeatureFlag] = useMutation(CREATE_FEATURE_FLAG);
  const [deleteFeatureFlag] = useMutation(DELETE_FEATURE_FLAG);
  const [updateFeatureFlag] = useMutation(UPDATE_FEATURE_FLAG);

  const handleCreateFlag = useCallback(async () => {
    if (!newFlagKey.trim() || !newFlagDescription.trim()) return;
    await createFeatureFlag({
      variables: {
        input: {
          key: newFlagKey.trim(),
          description: newFlagDescription.trim(),
          defaultValue: newFlagDefault.trim() || 'false',
        },
      },
    });
    setNewFlagKey('');
    setNewFlagDescription('');
    setNewFlagDefault('false');
    refetchFlags();
  }, [newFlagKey, newFlagDescription, newFlagDefault, createFeatureFlag, refetchFlags]);

  const handleDeleteFlag = useCallback(
    async (key: string) => {
      await deleteFeatureFlag({ variables: { key } });
      refetchFlags();
    },
    [deleteFeatureFlag, refetchFlags]
  );

  const handleToggleActive = useCallback(
    async (flag: FeatureFlagType) => {
      await updateFeatureFlag({
        variables: { key: flag.key, input: { isActive: !flag.isActive } },
      });
      refetchFlags();
    },
    [updateFeatureFlag, refetchFlags]
  );

  const handleStartEdit = useCallback((flag: FeatureFlagType) => {
    setEditingKey(flag.key);
    setEditDescription(flag.description);
    setEditDefaultValue(flag.defaultValue);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingKey(null);
    setEditDescription('');
    setEditDefaultValue('');
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingKey || !editDescription.trim() || !editDefaultValue.trim()) return;
    await updateFeatureFlag({
      variables: {
        key: editingKey,
        input: {
          description: editDescription.trim(),
          defaultValue: editDefaultValue.trim(),
        },
      },
    });
    setEditingKey(null);
    refetchFlags();
  }, [editingKey, editDescription, editDefaultValue, updateFeatureFlag, refetchFlags]);

  return (
    <Container>
      {/* Tenants list */}
      <AdminSection>
        <Typography size="L" variant="semiBold">
          {t('pages:admin.tenants')}
        </Typography>
        {loaderData.tenants.map((tenant) => (
          <TenantRow
            key={tenant.id}
            onClick={() => navigate(`tenants/${tenant.id}`)}
          >
            <TenantInfo>
              <Typography size="S" variant="semiBold">
                {tenant.name}
              </Typography>
              <Typography size="XXS" variant="regular" colorText="greySilver">
                {tenant.code}
              </Typography>
            </TenantInfo>
            <StatusBadge $active={tenant.isActive}>
              {tenant.isActive
                ? t('pages:admin.active')
                : t('pages:admin.inactive')}
            </StatusBadge>
          </TenantRow>
        ))}
      </AdminSection>

      {/* Create new feature flag */}
      <AdminSection>
        <Typography size="L" variant="semiBold">
          {t('pages:admin.createFeatureFlag')}
        </Typography>
        <InputsContainer>
          <InputText
            label={t('pages:admin.flagKey')}
            typeInput="normal"
            inputSize="M"
            name="flagKey"
            placeholder="feature_name"
            value={newFlagKey}
            onChange={(e) => setNewFlagKey(e.target.value)}
          />
          <InputText
            label={t('pages:admin.flagDescription')}
            typeInput="normal"
            inputSize="L"
            name="flagDescription"
            placeholder={t('pages:admin.flagDescription')}
            value={newFlagDescription}
            onChange={(e) => setNewFlagDescription(e.target.value)}
          />
          <InputText
            label={t('pages:admin.flagDefaultValue')}
            typeInput="normal"
            inputSize="S"
            name="flagDefault"
            placeholder="false"
            value={newFlagDefault}
            onChange={(e) => setNewFlagDefault(e.target.value)}
          />
          <MainButton
            variant="primary"
            text={t('pages:admin.addFlag')}
            onClick={handleCreateFlag}
            disabled={!newFlagKey.trim() || !newFlagDescription.trim()}
          />
        </InputsContainer>
      </AdminSection>

      {/* Global feature flag catalog */}
      <AdminSection>
        <Typography size="L" variant="semiBold">
          {t('pages:admin.flagCatalog')}
        </Typography>
        {featureFlags.length === 0 ? (
          <Typography size="S" variant="regular" colorText="greySilver">
            {t('pages:admin.noFlags')}
          </Typography>
        ) : (
          featureFlags.map((flag) => (
            <FlagRow key={flag.id}>
              <FlagInfo>
                <Typography size="XS" variant="medium">
                  {flag.key}
                </Typography>
                {editingKey === flag.key ? (
                  <FlagEditFields>
                    <InputText
                      label={t('pages:admin.flagDescription')}
                      typeInput="normal"
                      inputSize="L"
                      name="editFlagDescription"
                      placeholder={t('pages:admin.flagDescription')}
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                    />
                    <InputText
                      label={t('pages:admin.flagDefaultValue')}
                      typeInput="normal"
                      inputSize="L"
                      name="editFlagDefaultValue"
                      placeholder="false"
                      value={editDefaultValue}
                      onChange={(e) => setEditDefaultValue(e.target.value)}
                    />
                  </FlagEditFields>
                ) : (
                  <>
                    <Typography size="XXS" variant="regular" colorText="greySilver">
                      {flag.description}
                    </Typography>
                    <Typography size="XXS" variant="regular" colorText="greySilver">
                      {t('pages:admin.flagDefaultValue')}: {flag.defaultValue}
                    </Typography>
                    {!flag.isActive && (
                      <Typography size="XXS" variant="regular" colorText="greySilver">
                        {t('pages:admin.flagActiveHint')}
                      </Typography>
                    )}
                  </>
                )}
              </FlagInfo>
              <FlagActions>
                {editingKey === flag.key ? (
                  <>
                    <MainButton
                      variant="primary"
                      text={t('buttons:save')}
                      onClick={handleSaveEdit}
                      disabled={!editDescription.trim() || !editDefaultValue.trim()}
                    />
                    <MainButton
                      variant="secondary"
                      text={t('buttons:cancel')}
                      onClick={handleCancelEdit}
                    />
                  </>
                ) : (
                  <>
                    <SimpleSwitch
                      state={flag.isActive}
                      changeState={() => handleToggleActive(flag)}
                    />
                    <MainButton
                      variant="secondary"
                      text={t('buttons:edit')}
                      onClick={() => handleStartEdit(flag)}
                    />
                    <MainButton
                      variant="secondary"
                      text={t('pages:admin.deleteFlag')}
                      onClick={() => handleDeleteFlag(flag.key)}
                    />
                  </>
                )}
              </FlagActions>
            </FlagRow>
          ))
        )}
      </AdminSection>
    </Container>
  );
}
